import KeyboardKeyIcon from '@/components/ui/keyboard-key-icon';
import { toast } from '@/hooks/use-toast';
import { HOTKEYS } from '@/lib/hotkeys';
import { pdkAxios } from '@/lib/pdk-axios';
import { cn } from '@/lib/utils';
import { vehicleFromIndexerStore } from '@/models/stores/vehicle-listings';
import type { VehicleFromIndexer } from '@/models/types';
import { useMutation } from '@tanstack/react-query';
import { useMemo, useRef } from 'react';
import {
  ComboboxPopover,
  type Option,
} from '../../ui/combobox/combobox-popover';
import { AutopilotBadge } from './autopilot-badge';

export const getAutopilotOptions = (): Option<string>[] => {
  return [
    {
      value: 'None',
      label: 'None',
    },
    {
      value: 'Driver Assistance Package',
      label: 'Driver Assistance Package',
    },
    {
      value: 'Active Driving Assistant Pro',
      label: 'BMW / Active Driving Assistant Pro',
    },
    {
      value: 'Chevy Safety Assist',
      label: 'Chevrolet / Chevy Safety Assist',
    },
    {
      value: 'SuperCruise',
      label: 'Chevrolet / SuperCruise',
    },
    {
      value: 'FI-Pilot',
      label: 'Fisker / FI-Pilot',
    },
    {
      value: 'Co-Pilot 360',
      label: 'Ford / Co-Pilot 360',
    },
    {
      value: 'Blue Cruise 1.0',
      label: 'Ford / Blue Cruise 1.0',
    },
    {
      value: 'Blue Cruise 1.2',
      label: 'Ford / Blue Cruise 1.2',
    },
    {
      value: 'Hyundai Driving Assistant',
      label: 'Hyundai / Hyundai Driving Assistant',
    },
    {
      value: 'Hyundai Driving Assistant II',
      label: 'Hyundai / Hyundai Driving Assistant II',
    },
    {
      value: 'DreamDrive Standard',
      label: 'Lucid / DreamDrive Standard',
    },
    {
      value: 'DreamDrive Premium',
      label: 'Lucid / DreamDrive Premium',
    },
    {
      value: 'DreamDrive Pro',
      label: 'Lucid / DreamDrive Pro',
    },
    {
      value: 'DISTRONIC Adaptive Cruise Control',
      label: 'Mercedes / DISTRONIC Adaptive Cruise Control',
    },
    {
      value: 'ProPilot Assist',
      label: 'Nissan / ProPilot Assist',
    },
    {
      value: 'ProPilot Assist 2.0',
      label: 'Nissan / ProPilot Assist 2.0',
    },
    {
      value: 'Nissan Safety Shield 360',
      label: 'Nissan / Nissan Safety Shield 360',
    },
    {
      value: 'Pilot Assist',
      label: 'Polestar or Volvo / Pilot Assist',
    },
    {
      value: 'InnoDrive incl. ACC and ALK',
      label: 'Porsche / InnoDrive incl. ACC and ALK',
    },
    {
      value: 'Driver+',
      label: 'Rivian / Driver+',
    },
    {
      value: 'Autopilot',
      label: 'Tesla / Autopilot or Self-Driving',
    },
    {
      value: 'Enhanced Autopilot',
      label: 'Tesla / Enhanced Autopilot',
    },
    {
      value: 'Full Self Driving',
      label: 'Tesla / Full Self Driving',
    },
    {
      value: 'Navigate on Autosteer',
      label: 'Tesla / Navigate on Autosteer',
    },
    {
      value: 'Traffic Aware Cruise Control & Autosteer',
      label: 'Tesla / Traffic Aware Cruise Control & Autosteer',
    },
    {
      value: 'IQ.Drive',
      label: 'Volkswagen / IQ.Drive',
    },
  ];
};

export const AUTOPILOT_OPTIONS_MAP = getAutopilotOptions();

export type AutopilotSelectorProps = {
  id: string;
  value: VehicleFromIndexer['autopilot_features'] | null;
  hideCopy?: boolean;
  side?: 'left' | 'right' | 'bottom' | 'top';
  buttonVariant?: 'outline' | 'ghost';
  className?: string;
};

export function AutopilotSelector({
  id,
  value,
  hideCopy = false,
  side = 'left',
  buttonVariant = 'ghost',
  className,
}: AutopilotSelectorProps) {
  const autopilotButtonRef = useRef<HTMLButtonElement>(null);

  const updateAutopilotMutation = useMutation({
    onMutate: async ({ autopilot }) => {
      vehicleFromIndexerStore.updateAutopilot({ id, autopilot });
    },
    mutationFn: async ({ autopilot }: { autopilot: string }) => {
      return await pdkAxios.post(
        `/inventory/vehicles/${id}/autopilot_features`,
        {
          autopilot_features: autopilot,
        },
      );
    },
    onError: (error) => {
      toast({
        title: 'Error updating autopilot',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const handleAutopilotChange = (
    newAutopilot: VehicleFromIndexer['autopilot_features'],
  ) => {
    updateAutopilotMutation.mutate({ autopilot: newAutopilot });
  };

  const currentOption = useMemo(() => {
    return AUTOPILOT_OPTIONS_MAP.find((option) => option.value === value);
  }, [value]);

  return (
    <div>
      <ComboboxPopover
        endAdornment={
          <KeyboardKeyIcon>{HOTKEYS.VEHICLE_AUTOPILOT}</KeyboardKeyIcon>
        }
        placeholder="Set autopilot..."
        currentOption={currentOption}
        onChange={handleAutopilotChange}
        emptyMessage="No autopilot found"
        options={AUTOPILOT_OPTIONS_MAP}
        side={side}
        align="start"
        onKeyDown={(e) => {
          const keyPressed = e.key;

          const option = AUTOPILOT_OPTIONS_MAP.find(
            (option) => option.hotkey === keyPressed,
          );

          if (option) {
            handleAutopilotChange(option.value);
            e.preventDefault();
            e.stopPropagation();
          }
        }}
      >
        <div>
          <AutopilotBadge
            value={value ?? 'None'}
            hideCopy={hideCopy}
            buttonRef={autopilotButtonRef}
            buttonVariant={buttonVariant}
            className={cn('pl-0', className)}
          />
        </div>
      </ComboboxPopover>
    </div>
  );
}
